import { GridColDef } from '@mui/x-data-grid';

interface ExportToCSVOptions<T extends Record<string, any>> {
    rows: T[];
    columns: GridColDef[];
    includeColumns?: string[];
    additionalFields?: Array<{
        field: string;
        headerName: string;
        valueGetter?: (row: T) => string | number;
    }>;
    filename?: string;
}

export function exportToCSV<T extends Record<string, any>>(options: ExportToCSVOptions<T>): void {
    const {
        rows,
        columns,
        includeColumns = [],
        additionalFields = [],
        filename = 'exported-data',
    } = options;

    const activeColumns = includeColumns.length > 0
        ? columns.filter(col => includeColumns.includes(col.field))
        : columns;

    type ExportField = {
        field: string;
        headerName: string | undefined;
        valueGetter?: any;
    };

    const allFields: ExportField[] = [
        ...activeColumns.map(col => ({
            field: col.field,
            headerName: col.headerName || col.field,
            valueGetter: col.valueGetter,
        })),
        ...additionalFields.map(field => ({
            field: field.field,
            headerName: field.headerName,
            valueGetter: field.valueGetter,
        })),
    ];

    const headers = allFields.map(col => {
        const headerName = col.headerName ? 
            col.headerName.toString().replace(/"/g, '""') : col.field;
        return `"${headerName}"`;
    });

    const csvRows = rows.map(row => {
        const values = allFields.map(col => {
            let value: any = '';

            if (col.valueGetter && typeof col.valueGetter === 'function') {
                try {
                    const params: any = {
                        row,
                        value: row[col.field],
                        field: col.field,
                        id: row.id,
                        colDef: col as any,
                        api: undefined as any,
                        hasFocus: false,
                        tabIndex: -1,
                        formattedValue: row[col.field],
                    };

                    try {
                        value = col.valueGetter(params);
                    } catch (e) {
                        value = (col.valueGetter as (row: T) => string | number)(row);
                    }
                } catch (e) {
                    console.error(`Error getting value for field ${col.field}:`, e);
                    value = row[col.field] || '';
                }
            } else {
                value = row[col.field] || '';
            }

            if (value instanceof Date) {
                value = value.toLocaleString();
            } else if (typeof value === 'object' && value !== null) {
                value = JSON.stringify(value);
            }

            if (typeof value === 'string') {
                value = value.replace(/"/g, '""');
            }

            return `"${value}"`;
        });

        return values.join(',');
    });

    const csvContent = [headers.join(','), ...csvRows].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');

    const url = URL.createObjectURL(blob);
    link.href = url;
    link.setAttribute('download', `${filename}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
}
